import React, { FC, memo } from 'react';
import styled from '@emotion/styled';
import { useI18n } from '~Hooks';
import { useEposTheme } from '../../Providers/ThemeProvider';
import { StageLayer } from '../../styles';
import dineInIcon from '../../../../Assets/icons/EPOS/dine_in.svg';
import shoppingBagIcon from '../../../../Assets/icons/EPOS/shopping_bag.svg';
import BottomBar from './BottomBar';
import GetBack from '../../Components/GetBack';
import ServiceChoice from './ServiceChoice';
import ClosedNotice from './ClosedNotice';
import useVenueOpen from '../../Hooks/useVenueOpen';
import { EPOS_SERVICE_TYPE, SERVICE_TYPE } from '../../../../../__queries__';

/**
 * The welcome content. It sits on the shared photo stage (see styles/Stage.tsx)
 * rather than owning a backdrop of its own, so the menu sheet can fly up over
 * the same background. The two service-type choices sit in one bottom-anchored
 * action bar - within thumb reach on a portrait ELO kiosk, still centered on a
 * landscape one - as matching glass cards so neither reads as pre-selected.
 */
const Layer = styled(StageLayer)<{ visible: boolean }>`
  opacity: ${({ visible }) => (visible ? 1 : 0)};
  pointer-events: ${({ visible }) => (visible ? 'auto' : 'none')};
  transition: opacity 0.35s ease-out;
`;

const Content = styled.div`
  position: relative;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  padding: 72px 48px 48px;
  box-sizing: border-box;

  /* Portrait: pull the cards up off the very bottom edge, within thumb reach
     but not glued to the bottom bar. */
  @media (orientation: portrait) {
    padding-bottom: 15vh;
  }
`;

/** Venue icon from backoffice, top-center above the service cards. */
const IconCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const VenueIcon = styled.img`
  max-width: 280px;
  max-height: 160px;
  object-fit: contain;
  filter: drop-shadow(0 8px 24px rgba(0, 0, 0, 0.35));
`;

/** The two choices, bottom-anchored. */
const Actions = styled.div`
  width: 100%;
  max-width: 900px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Cards = styled.div`
  display: flex;
  flex-direction: row;
  gap: 24px;

  /** A lone "Order" card would span the full bar - keep it narrower and centered. */
  &.is-single {
    width: 100%;
    max-width: 560px;
    margin: 0 auto;
  }
`;

interface Props {
  /** Called once the chosen service type has been set on the cart. */
  onStart: () => void;
  /** Faded out - but kept mounted - while the menu sheet is up. */
  visible: boolean;
}

const Welcome: FC<Props> = ({ onStart, visible }) => {
  const { i18n } = useI18n();
  const eposTheme = useEposTheme();
  /** Only Welcome asks - `null` (still loading) shows the cards rather than flashing "closed". */
  const venueOpen = useVenueOpen();
  /** Set in backoffice -> only that service type is offered, as a single centered "Order" card. */
  const limitedToServiceType = eposTheme.limitedToServiceType;

  return (
    <Layer visible={visible}>
      {/* Hidden staff exit - four taps in the top left corner. */}
      <GetBack />

      <Content>
        {eposTheme.iconImageSrcUrl ? (
          <IconCard>
            <VenueIcon src={eposTheme.iconImageSrcUrl} alt="" draggable={false} />
          </IconCard>
        ) : (
          <div />
        )}

        <Actions>
          {venueOpen === false ? (
            <ClosedNotice />
          ) : (
            <Cards className={limitedToServiceType ? 'is-single' : undefined}>
              {limitedToServiceType ? (
                <ServiceChoice
                  // Same string values as SERVICE_TYPE (EAT_HERE/TAKE_AWAY) - ServiceChoice
                  // is shared with the unrestricted branch below, which passes that wider enum.
                  serviceType={(limitedToServiceType as unknown) as SERVICE_TYPE}
                  icon={
                    limitedToServiceType === EPOS_SERVICE_TYPE.TAKE_AWAY
                      ? shoppingBagIcon
                      : dineInIcon
                  }
                  label={i18n('Express.Order')}
                  onStart={onStart}
                  testId="epos-service-order"
                />
              ) : (
                <>
                  <ServiceChoice
                    serviceType={SERVICE_TYPE.EAT_HERE}
                    icon={dineInIcon}
                    label={i18n('ServiceType.EatHere')}
                    onStart={onStart}
                    testId="epos-service-eat-here"
                  />
                  <ServiceChoice
                    serviceType={SERVICE_TYPE.TAKE_AWAY}
                    icon={shoppingBagIcon}
                    label={i18n('ServiceType.TakeAway')}
                    onStart={onStart}
                    testId="epos-service-take-away"
                  />
                </>
              )}
            </Cards>
          )}
        </Actions>
      </Content>

      <BottomBar />
    </Layer>
  );
};

export default memo(Welcome);
